import { ArrowLeft, Mail, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import styles from "./LegalPage.module.css";
import Footer from "../../ui/Footer/Footer";
const sections = [
  { id: "intro", title: "Our Commitment" },
  { id: "info-collect", title: "Information We Collect" },
  { id: "how-use", title: "How We Use Your Information" },
  { id: "data-sharing", title: "Data Sharing & Disclosure" },
  { id: "data-security", title: "Data Security" },
  { id: "data-retention", title: "Data Retention" },
  { id: "your-rights", title: "Your Rights" },
  { id: "cookies", title: "Cookies & Tracking" },
  { id: "children", title: "Children's Privacy" },
  { id: "changes", title: "Changes to This Policy" },
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("intro");

  const handleClick = (id) => {
    setActiveSection(id);
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className={styles.page}>
      {/* Dark Hero Banner */}
      <div className={styles.heroBanner}>
        <div className={styles.heroInner}>
          <Link to="/" className={styles.backLink}>
            <ArrowLeft size={16} />
            Back to Home
          </Link>
          <h1 className={styles.heroTitle}>Privacy Statement</h1>
          <p className={styles.heroMeta}>Effective date: March 1, 2026</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className={styles.tabBar}>
        <div className={styles.tabBarInner}>
          <Link
            to="/privacy-policy"
            className={`${styles.tab} ${styles.tabActive}`}
          >
            Privacy Statement
          </Link>
          <Link to="/terms-of-service" className={styles.tab}>
            Terms of Service
          </Link>
        </div>
      </div>

      {/* Layout: Sidebar + Content */}
      <div className={styles.layout}>
        {/* Sidebar Navigation */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarTitle}>On this page</div>
          <nav className={styles.sidebarNav}>
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={`${styles.sidebarLink} ${activeSection === s.id ? styles.sidebarLinkActive : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(s.id);
                }}
              >
                {s.title}
              </a>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className={styles.mainContent}>
          {/* Intro */}
          <div className={styles.section} id="intro">
            <h2 className={styles.sectionTitle}>
              Our Commitment to Your Privacy
            </h2>
            <div className={styles.sectionBody}>
              <p>
                At <strong>viewStruct</strong>, we are committed to protecting
                your personal information and your right to privacy. This
                Privacy Statement explains what information we collect, how we
                use it, and what rights you have in relation to it.
              </p>
              <div className={styles.highlightBox}>
                This policy applies to all information collected through our
                platform, including the viewStruct web application, APIs, file
                storage services, and any related services or tools.
              </div>
            </div>
          </div>

          {/* 1. Information We Collect */}
          <div className={styles.section} id="info-collect">
            <h2 className={styles.sectionTitle}>Information We Collect</h2>
            <div className={styles.sectionBody}>
              <p>
                <strong>Personal Information You Provide:</strong>
              </p>
              <ul>
                <li>
                  Account registration data: name, email address, company name,
                  job title, and phone number
                </li>
                <li>
                  Profile information: avatar, bio, location, and professional
                  details
                </li>
                <li>
                  Payment and billing information processed through our secure
                  payment providers
                </li>
                <li>
                  Communication data: messages, feedback, and support requests
                  you send to us
                </li>
              </ul>
              <p>
                <strong>Information Collected Automatically:</strong>
              </p>
              <ul>
                <li>
                  Device and browser information (type, version, operating
                  system)
                </li>
                <li>IP address and approximate geographic location</li>
                <li>
                  Usage data: pages visited, features used, files uploaded, and
                  interaction patterns
                </li>
                <li>Log data: access times, error logs, and referral URLs</li>
              </ul>
              <p>
                <strong>Files and Content:</strong>
              </p>
              <ul>
                <li>
                  3D and 2D model files (IFC, RVT, DWG, and other CAD formats)
                  you upload to the platform
                </li>
                <li>
                  Project metadata, annotations, comments, and collaboration
                  data
                </li>
                <li>File version history and modification records</li>
              </ul>
            </div>
          </div>

          {/* 2. How We Use */}
          <div className={styles.section} id="how-use">
            <h2 className={styles.sectionTitle}>How We Use Your Information</h2>
            <div className={styles.sectionBody}>
              <p>
                We use the information we collect for the following purposes:
              </p>
              <ul>
                <li>
                  <strong>Service delivery:</strong> To provide, operate, and
                  maintain the viewStruct platform, including model viewing,
                  file management, and collaboration tools
                </li>
                <li>
                  <strong>Account management:</strong> To create and manage your
                  account, process transactions, and send related information
                </li>
                <li>
                  <strong>Communication:</strong> To send you technical notices,
                  updates, security alerts, and support messages
                </li>
                <li>
                  <strong>Improvement:</strong> To analyze usage patterns and
                  improve our platform, features, and user experience
                </li>
                <li>
                  <strong>Security:</strong> To detect, prevent, and address
                  technical issues, fraud, and unauthorized access
                </li>
                <li>
                  <strong>Legal compliance:</strong> To comply with applicable
                  laws, regulations, and legal processes
                </li>
              </ul>
              <div className={styles.highlightBox}>
                We do not sell your personal information to third parties. We do
                not use your uploaded CAD/BIM files for any purpose other than
                providing you with our services.
              </div>
            </div>
          </div>

          {/* 3. Data Sharing */}
          <div className={styles.section} id="data-sharing">
            <h2 className={styles.sectionTitle}>Data Sharing & Disclosure</h2>
            <div className={styles.sectionBody}>
              <p>
                We may share your information only in the following
                circumstances:
              </p>
              <ul>
                <li>
                  <strong>Service providers:</strong> With trusted third-party
                  vendors who assist us in operating our platform (cloud
                  hosting, analytics, payment processing) under strict
                  confidentiality agreements
                </li>
                <li>
                  <strong>Team collaboration:</strong> With other members of
                  your organization or project team, as configured by your
                  account administrator
                </li>
                <li>
                  <strong>Legal requirements:</strong> When required by law,
                  regulation, or legal process, or to protect the rights,
                  property, or safety of viewStruct, our users, or the public
                </li>
                <li>
                  <strong>Business transfers:</strong> In connection with any
                  merger, acquisition, or sale of company assets, your data may
                  be transferred as part of the transaction
                </li>
              </ul>
            </div>
          </div>

          {/* 4. Data Security */}
          <div className={styles.section} id="data-security">
            <h2 className={styles.sectionTitle}>Data Security</h2>
            <div className={styles.sectionBody}>
              <p>
                We implement industry-standard security measures to protect your
                data:
              </p>
              <ul>
                <li>
                  <strong>Encryption:</strong> All data is encrypted in transit
                  (TLS 1.3) and at rest (AES-256)
                </li>
                <li>
                  <strong>Access controls:</strong> Role-based access controls
                  and multi-factor authentication for all internal systems
                </li>
                <li>
                  <strong>Infrastructure:</strong> Our servers are hosted in SOC
                  2 Type II certified data centers with 24/7 monitoring
                </li>
                <li>
                  <strong>Audits:</strong> Regular security audits, penetration
                  testing, and vulnerability assessments
                </li>
                <li>
                  <strong>Incident response:</strong> A dedicated security team
                  with established breach notification procedures
                </li>
              </ul>
              <div className={styles.highlightBox}>
                While we strive to protect your data, no method of electronic
                storage or transmission is 100% secure. We encourage you to use
                strong passwords and enable two-factor authentication on your
                account.
              </div>
            </div>
          </div>

          {/* 5. Data Retention */}
          <div className={styles.section} id="data-retention">
            <h2 className={styles.sectionTitle}>Data Retention</h2>
            <div className={styles.sectionBody}>
              <p>
                We retain your information for as long as your account is active
                or as needed to provide you services:
              </p>
              <ul>
                <li>
                  <strong>Account data:</strong> Retained for the duration of
                  your account and up to 30 days after deletion request
                </li>
                <li>
                  <strong>Uploaded files:</strong> Retained until you delete
                  them or close your account. Deleted files are permanently
                  removed within 90 days
                </li>
                <li>
                  <strong>Usage logs:</strong> Retained for up to 12 months for
                  analytics and security purposes
                </li>
                <li>
                  <strong>Billing records:</strong> Retained for up to 7 years
                  as required by tax and financial regulations
                </li>
              </ul>
            </div>
          </div>

          {/* 6. Your Rights */}
          <div className={styles.section} id="your-rights">
            <h2 className={styles.sectionTitle}>Your Rights</h2>
            <div className={styles.sectionBody}>
              <p>
                Depending on your location, you may have the following rights
                regarding your personal data:
              </p>
              <ul>
                <li>
                  <strong>Access:</strong> Request a copy of the personal data
                  we hold about you
                </li>
                <li>
                  <strong>Correction:</strong> Request that we correct any
                  inaccurate or incomplete data
                </li>
                <li>
                  <strong>Deletion:</strong> Request that we delete your
                  personal data, subject to legal retention requirements
                </li>
                <li>
                  <strong>Portability:</strong> Request a machine-readable copy
                  of your data for transfer to another service
                </li>
                <li>
                  <strong>Restriction:</strong> Request that we restrict the
                  processing of your data in certain circumstances
                </li>
                <li>
                  <strong>Objection:</strong> Object to our processing of your
                  data for direct marketing purposes
                </li>
                <li>
                  <strong>Withdraw consent:</strong> Where processing is based
                  on consent, you may withdraw it at any time
                </li>
              </ul>
              <p>
                To exercise any of these rights, please contact us at{" "}
                <strong>privacy@viewstruct.com</strong>. We will respond to your
                request within 30 days.
              </p>
            </div>
          </div>

          {/* 7. Cookies */}
          <div className={styles.section} id="cookies">
            <h2 className={styles.sectionTitle}>
              Cookies & Tracking Technologies
            </h2>
            <div className={styles.sectionBody}>
              <p>
                We use cookies and similar technologies to enhance your
                experience:
              </p>
              <ul>
                <li>
                  <strong>Essential cookies:</strong> Required for the platform
                  to function (authentication, session management, security)
                </li>
                <li>
                  <strong>Analytics cookies:</strong> Help us understand how
                  users interact with our platform to improve features and
                  performance
                </li>
                <li>
                  <strong>Preference cookies:</strong> Remember your settings,
                  language, and display preferences
                </li>
              </ul>
              <p>
                You can manage your cookie preferences through your browser
                settings. Disabling essential cookies may affect the
                functionality of the platform.
              </p>
            </div>
          </div>

          {/* 8. Children */}
          <div className={styles.section} id="children">
            <h2 className={styles.sectionTitle}>Children's Privacy</h2>
            <div className={styles.sectionBody}>
              <p>
                viewStruct is designed for professional use and is not intended
                for children under the age of 16. We do not knowingly collect
                personal information from children. If we become aware that we
                have collected data from a child under 16, we will take steps to
                delete it promptly.
              </p>
            </div>
          </div>

          {/* 9. Changes */}
          <div className={styles.section} id="changes">
            <h2 className={styles.sectionTitle}>Changes to This Policy</h2>
            <div className={styles.sectionBody}>
              <p>
                We may update this Privacy Statement from time to time. We will
                notify you of any significant changes by posting a notice on our
                platform and sending an email to your registered address. Your
                continued use of the platform after changes become effective
                constitutes acceptance of the revised policy.
              </p>
            </div>
          </div>
        </main>
      </div>

      {/* Contact Footer */}
      {/* <div className={styles.contactFooter}>
        <div className={styles.contactFooterInner}>
          <div className={styles.contactFooterText}>
            <h3>Questions About Your Privacy?</h3>
            <p>Our privacy team is here to help with any questions.</p>
          </div>
          <div className={styles.contactFooterLinks}>
            <button className={styles.contactBtn}>
              <Mail size={15} />
              privacy@viewstruct.com
            </button>
            <button
              className={`${styles.contactBtn} ${styles.contactBtnOutline}`}
            >
              <Globe size={15} />
              Support Center
            </button>
          </div>
        </div>
      </div> */}

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
